import React, { useEffect, useMemo, useState } from 'react';
import { formatMoney, toCents, fromCents } from '../../../utils/tripPaymentSummary';

/**
 * Final fare for a METERED Itinerary trip (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md
 * §"Itinerary payment flow": arrived → final fare UI → PRICE → PRICE|Ok → payment).
 *
 * Itinerary has no soft meter, so the driver enters the metered total here. The
 * owner (ItineraryDispatch) turns it into the trip's meter price row and sends
 * `PRICE|<TripNo>|items`, then waits for dispatch's PRICE|Ok before the Collect
 * Payment sheet opens — a metered trip is never collected or completed on a
 * fare dispatch has not accepted. Rate context (flag drop, per-unit rate) comes
 * from `reservation.meter` / `reservation.wait` when the server supplied them.
 */
function ItineraryFinalFareModal({
  open,
  waypoint,
  pending = false,
  error = null,
  onConfirm,
  onCancel,
}) {
  const reservation = waypoint?.trip?.reservation || null;
  const meter = reservation?.meter || null;
  const wait = reservation?.wait || null;
  const tripNumber = reservation?.number || waypoint?.tripNo || waypoint?.TripNo || '';
  const passengerName = waypoint?.passengerName || waypoint?.trip?.passenger?.name || '';

  const suggested = useMemo(() => {
    const price = Number(reservation?.price);
    if (Number.isFinite(price) && price > 0) return price.toFixed(2);
    return '';
  }, [reservation?.price]);

  const [amount, setAmount] = useState(suggested);
  useEffect(() => {
    if (open) setAmount(suggested);
  }, [open, suggested, waypoint?.TripId]);

  if (!open) return null;

  const cents = toCents(amount);
  const valid = /^\d+(\.\d{1,2})?$/.test(String(amount).trim()) && cents >= 0;

  const submit = (e) => {
    e?.preventDefault?.();
    if (!valid || pending) return;
    onConfirm?.(fromCents(cents));
  };

  return (
    <div className="fixed inset-0 z-[1400] flex items-end sm:items-center justify-center bg-black/50 p-3" role="dialog" aria-modal="true" aria-label="Final fare">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
        <div className="px-5 pt-4 pb-3 border-b border-gray-200">
          <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Metered trip</div>
          <h2 className="text-lg font-bold text-gray-900">Confirm final fare</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {tripNumber ? `Trip #${tripNumber}` : 'Trip'}{passengerName ? ` · ${passengerName}` : ''}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3">
          {(meter || wait) && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 space-y-1">
              {meter && Number(meter.flag) > 0 && (
                <div className="flex justify-between"><span>Flag drop</span><span>{formatMoney(meter.flag)}</span></div>
              )}
              {meter && Number(meter.distance) > 0 && (
                <div className="flex justify-between"><span>Per distance unit</span><span>{formatMoney(meter.distance)}</span></div>
              )}
              {meter && Number(meter.minimumFare) > 0 && (
                <div className="flex justify-between"><span>Minimum fare</span><span>{formatMoney(meter.minimumFare)}</span></div>
              )}
              {wait && Number(wait.timeUnitRate) > 0 && (
                <div className="flex justify-between"><span>Wait time rate</span><span>{formatMoney(wait.timeUnitRate)}</span></div>
              )}
            </div>
          )}

          <label className="block text-sm font-semibold text-gray-800">
            Metered fare ($)
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={amount}
              disabled={pending}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              aria-label="Metered fare amount"
              aria-invalid={!valid && amount !== ''}
              className={`mt-1 w-full rounded-xl border px-3 py-3 text-2xl font-bold text-right ${!valid && amount !== '' ? 'border-red-400' : 'border-gray-300'}`}
            />
          </label>
          <p className="text-xs text-gray-500">
            Dispatch records this as the trip's meter fare. Tolls, card fees and gratuity
            already configured for the trip are added by dispatch, not here.
          </p>

          {pending && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Sending the fare to dispatch…
            </p>
          )}
          {error && !pending && (
            <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{error}</p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="flex-1 rounded-xl border border-gray-300 bg-white text-gray-700 font-semibold py-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || pending}
            className="flex-1 rounded-xl bg-emerald-600 text-white font-semibold py-3 disabled:opacity-50"
          >
            {error && !pending ? 'Retry' : 'Confirm & Collect Payment'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ItineraryFinalFareModal;
